import { StripeValidationStatus } from "src/page/stripeTransactionHistory/stripeTransactionHistorySlice";
import { AccountStatus, ReimbursementStatus } from "src/types/commonTypes";

type ColorsByLetter = {
  [key: string]: string;
};

export const colorsByLetter: ColorsByLetter = {
  A: "#ADEFD1FF",
  B: "#00539CFF",
  C: "#2C5F2D",
  D: "#97BC62FF",
  E: "#89ABE3FF",
  F: "#A07855FF",
  G: "#333D79FF",
  H: "#F93822FF",
  I: "#00B1D2FF",
  J: "#9FC131FF",
  K: "#D7A9E3FF",
  L: "#8BBEE8FF",
  M: "#A8D5BAFF",
  N: "#FFA351FF",
  O: "#FFBE7BFF",
  P: "#EED971FF",
  Q: "#408EC6",
  R: "#7A2048",
  S: "#1E2761",
  T: "#28334AFF",
  U: "#E3B448",
  V: "#B85042",
  W: "#4F3466FF",
  X: "#A7BEAE",
  Y: "#6E8898FF",
  Z: "#9F6B99FF"
};

// Fixed avatar tints asked for by the client: every user avatar is the same
// light green, every client/organisation avatar the same blue (the letter-based
// palette above still drives everything else).
export const USER_AVATAR_COLOR = colorsByLetter.D;
export const ORG_AVATAR_COLOR = colorsByLetter.Q;

export const bgColorbyName = (firstName: string) => {
  const firstLetter = firstName?.charAt(0).toUpperCase();

  return colorsByLetter[firstLetter] || "#A2A2A1FF";
};

export const roleColors: { [key: string]: string } = {
  ADMIN: "#007bff",
  ORGANISATION_SUPER_ADMIN: "#28a745",
  ORGANISATION_OPERATIONS_MANAGER: "#b45309",
  ORGANISATION_FINANCE_MANAGER: "#dc3545",
  GUARDIAN: "#17a589",
  PARTICIPANT: "#8e44ad",
  SUPPORTER: "#00bcd4",
  SITE: "#edaa1a",
  NOROLE: "#808080"
};

export const organisationTypeColors: { [key: string]: string } = {
  CORPORATE: "#2563eb",
  INDIVIDUAL: "#0284c7",
  REFERER: "#7c3aed",
  MERCHANT: "#0891b2"
};

export const cardStatusColors: { [key: string]: string } = {
  ACTIVE: "#22c55e",
  INACTIVE: "#f97316",
  BLOCKED: "#f43f5e",
  DRAFT: "#3b82f6",
  CANCELED: "#94a3b8",
  HOLD: "#f59e0b",
  ORDERED: "#3b3b6b"
};

export const typeColors: { [key: string]: string } = {
  GENERAL: "#008080",
  SPENDING: "#17a589",
  DIGITAL: "#007bff",
  GROUP: "#3b3b6b",
  SERVICE: "#8e44ad",
  CREDIT: "#00bcd4",
  CASH: "#d97706"
};

export const recurringTransferTypeColors: { [key: string]: string } = {
  FIXED: "#8e44ad",
  TOPUP: "#17a589"
};

export const groupColors: { [key: string]: string } = {
  SITE: "#008080",
  COST_CENTRE: "#7b3283"
};

export const cardTypeColors: { [key: string]: string } = {
  SHARED: "#873131",
  PRIVATE: "#58af53"
};

export const stageColors: { [key: string]: string } = {
  STAGE_1: "#4b7507",
  STAGE_2: "#b8b308",
  STAGE_3: "#e46161",
  STAGE_4: "#0e0e0e",
  STAGE_5: "#0e0e0e"
};

export const filterTagColor: { [key: string]: string } = {
  wallet: "#007bff",
  wallets: "#007bff",
  sourceWallet: "#007bff",
  destinationWallet: "#0f766e",
  requestedBy: "#28a745",
  card: "#8e44ad",
  cards: "#8e44ad",
  user: "#28a745",
  users: "#28a745",
  group: "#4caf50",
  groups: "#4caf50",
  date: "#dc3545",
  startDate: "#ffc107",
  endDate: "#dc3545",
  minAmount: "#17a589",
  maxAmount: "#8e44ad",
  amount: "#8e44ad",
  activityName: "#00bcd4",
  status: "#408EC6",
  accountStatus: "#408EC6",
  type: "#9bff00",
  types: "#9bff00",
  category: "#ff6f61",
  standard: "#8e44ad",
  receiptAvailable: "#ff9800",
  activityStatus: "#6a1b9a",
  xero_account_exist: "#1976d2",
  xero_bank_transaction_exist: "#43a047",
  sortBy: "#6c63ff",
  actionRequired: "#2563eb"
};

export const activityNameColors: { [key: string]: { background: string; border: string; color: string } } = {
  NOVATTI_CARD_TRANSACTION: {
    background: "rgba(124, 58, 237, 0.1)",
    border: "rgba(124, 58, 237, 0.25)",
    color: "#6d28d9"
  },
  NOVATTI_DAILY_TRANSACTION: {
    background: "rgba(14, 165, 233, 0.1)",
    border: "rgba(14, 165, 233, 0.25)",
    color: "#0369a1"
  },
  WALLET_TRANSACTION: {
    background: "rgba(20, 184, 166, 0.1)",
    border: "rgba(20, 184, 166, 0.25)",
    color: "#0f766e"
  },
  CASH_TRANSACTION: {
    background: "rgba(245, 158, 11, 0.1)",
    border: "rgba(245, 158, 11, 0.25)",
    color: "#b45309"
  },
  DEPOSIT: {
    background: "rgba(34, 197, 94, 0.1)",
    border: "rgba(34, 197, 94, 0.25)",
    color: "#15803d"
  },
  CREATE_USER: {
    background: "rgba(99, 102, 241, 0.1)",
    border: "rgba(99, 102, 241, 0.25)",
    color: "#4338ca"
  }
};

export const expenseTypeColors: { [key: string]: string } = {
  OFFLINE: "#008080",
  GROUP: "#008080",
  BILL: "#17a589",
  CARD: "#007bff"
};

export const gstTypeColors: { [key: string]: string } = {
  NONE: "#64748b",
  INCLUDED: "#16a34a",
  EXCLUDED: "#dc2626"
};

export const stripeStatusColors: Record<StripeValidationStatus, { background: string; color: string }> = {
  processing: { background: "#E0F2FE", color: "#0F172A" },
  success: { background: "#DCFCE7", color: "#14532D" },
  failure: { background: "#FEE2E2", color: "#7F1D1D" },
  pending: { background: "#FEF3C7", color: "#92400E" },
  refunded: { background: "#DBEAFE", color: "#1D4ED8" }
};

export const orderStatusColors: { [key: string]: string } = {
  PENDING: "#F59E0B",
  PAID: "#10B981",
  FAILED: "#EF4444",
  CANCELLED: "#6B7280",
  REFUNDED: "#3B82F6"
};

export const reimbursementStatusColors: Record<
  ReimbursementStatus,
  { background: string; border: string; color: string }
> = {
  DRAFT: { background: "rgba(245, 158, 11, 0.08)", border: "#fcd34d", color: "#92400e" },
  SUBMITTED: { background: "rgba(59, 130, 246, 0.08)", border: "#93c5fd", color: "#1e40af" },
  APPROVED: { background: "rgba(34, 197, 94, 0.08)", border: "#86efac", color: "#166534" },
  REJECTED: { background: "rgba(239, 68, 68, 0.08)", border: "#fca5a5", color: "#991b1b" },
  SETTLED: { background: "rgba(16, 185, 129, 0.08)", border: "#6ee7b7", color: "#065f46" },
  CANCELLED: { background: "rgba(100, 116, 139, 0.08)", border: "#cbd5e1", color: "#475569" },
  FAILED: { background: "rgba(239, 68, 68, 0.08)", border: "#fca5a5", color: "#991b1b" }
};

// Wallet Transfer Request lifecycle. Unknown statuses fall back to neutral via wtrStatusFallbackColor.
export const wtrStatusFallbackColor = { background: "rgba(100, 116, 139, 0.08)", border: "#cbd5e1", color: "#475569" };

export const wtrStatusColors: Record<string, { background: string; border: string; color: string }> = {
  PENDING: { background: "rgba(245, 158, 11, 0.08)", border: "#fcd34d", color: "#92400e" },
  PENDING_APPROVAL: { background: "rgba(245, 158, 11, 0.08)", border: "#fcd34d", color: "#92400e" },
  APPROVED: { background: "rgba(59, 130, 246, 0.08)", border: "#93c5fd", color: "#1e40af" },
  PROCESSING: { background: "rgba(59, 130, 246, 0.08)", border: "#93c5fd", color: "#1e40af" },
  COMPLETED: { background: "rgba(16, 185, 129, 0.08)", border: "#6ee7b7", color: "#065f46" },
  SETTLED: { background: "rgba(16, 185, 129, 0.08)", border: "#6ee7b7", color: "#065f46" },
  FAILED: { background: "rgba(239, 68, 68, 0.08)", border: "#fca5a5", color: "#991b1b" },
  REJECTED: { background: "rgba(239, 68, 68, 0.08)", border: "#fca5a5", color: "#991b1b" },
  CANCELLED: wtrStatusFallbackColor
};

export const entitlementStatusColors: { [key: string]: { background: string; color: string } } = {
  ACTIVE: { background: "#dcfce7", color: "#16a34a" },
  SUSPENDED: { background: "#fee2e2", color: "#dc2626" },
  INACTIVE: { background: "#f3f4f6", color: "#6b7280" }
};

export const accountStatusColors: Record<
  AccountStatus,
  { background: string; border: string; color: string; arrowColor: string }
> = {
  DRAFT: {
    background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    border: "#fcd34d",
    color: "#92400e",
    arrowColor: "#fef3c7"
  },
  SUBMITTED: {
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "#93c5fd",
    color: "#1e40af",
    arrowColor: "#dbeafe"
  },
  APPROVED: {
    background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
    border: "#6ee7b7",
    color: "#065f46",
    arrowColor: "#d1fae5"
  },
  READY: {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    border: "#94a3b8",
    color: "#475569",
    arrowColor: "#f1f5f9"
  },
  REJECTED: {
    background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
    border: "#fca5a5",
    color: "#991b1b",
    arrowColor: "#fee2e2"
  }
};
